<template>
  <div class="tinymce-wrapper">
    <editor
      :init="editorConfig"
      v-model="editorValue"
      tinymce-script-src="/tinymce/tinymce.min.js"
    />
  </div>
</template>

<script setup>
import { ref, watch } from 'vue';

// 1. Import ตัว Editor component
import Editor from '@tinymce/tinymce-vue';

// 2. Import CSS ของ TinyMCE ที่จำเป็น
// CSS สำหรับ UI หลักของ Editor (เช่น toolbar, menu)
import 'tinymce/skins/ui/tinymce-5/skin.min.css'; // oxide, oxide-dark,tinymce-5, tinymce-5-dark

// 3. Import ส่วนประกอบหลักของ TinyMCE
import 'tinymce/tinymce'; // Core
import 'tinymce/models/dom'; // Model
import 'tinymce/themes/silver'; // Theme
import 'tinymce/icons/default'; // Icons

// 4. Import ปลั๊กอินที่เราต้องการใช้งาน (ต้อง import ทุกตัวที่จะใช้)
import 'tinymce/plugins/lists';
import 'tinymce/plugins/link';
import 'tinymce/plugins/image';
import 'tinymce/plugins/table';
import 'tinymce/plugins/code';
import 'tinymce/plugins/wordcount';

const props = defineProps({
  content: {
    type: String,
    default: ''
  }
});
const emit = defineEmits(['update:content']);

// ตัวแปรสำหรับเก็บเนื้อหา
const editorValue = ref(null);

const normalizeContentColors = (html) => {
  if (!html || typeof html !== 'string') return html;

  let out = html.replace(/#f1c40f/gi, '#3859d0');

  // Ensure h2 headings use the brand color (persisted in saved HTML)
  out = out.replace(/<h2(?![^>]*\sstyle=)([^>]*)>/gi, '<h2$1 style="color: #3859d0;">');
  out = out.replace(/<h2([^>]*?)\sstyle="([^"]*)"([^>]*)>/gi, (match, pre, styleValue, post) => {
    if (/(^|;)\s*color\s*:/i.test(styleValue)) return match;
    const nextStyle = `${styleValue}`.trim().replace(/;?$/, '; ') + 'color: #3859d0;';
    return `<h2${pre} style="${nextStyle}"${post}>`;
  });

  out = out.replace(/<h3(?![^>]*\sstyle=)([^>]*)>/gi, '<h3$1 style="font-weight: 400;">');
  out = out.replace(/<h3([^>]*?)\sstyle="([^"]*)"([^>]*)>/gi, (match, pre, styleValue, post) => {
    if (/(^|;)\s*font-weight\s*:/i.test(styleValue)) return match;
    const nextStyle = `${styleValue}`.trim().replace(/;?$/, '; ') + 'font-weight: 400;';
    return `<h3${pre} style="${nextStyle}"${post}>`;
  });

  return out;
};

// 5. ตั้งค่า Editor
const editorConfig = {
  path_absolute: "/",
  height: 500,
  menubar: 'edit insert format table',
  plugins: 'lists link image table code wordcount',
  toolbar: 'undo redo | fontfamily | fontsize  | bold italic | backcolor forecolor | alignleft aligncenter alignright | bullist numlist | link image | code',
  promotion: false,
  branding: false,
  skin: false,
  content_css: false,
  content_style: 'h2 { color: #3859d0; } h3 { font-weight: 400; font-size: 14px; }',

  setup: (editor) => {
    const PLACEHOLDER = 'Font';
    let observer;
    const replaceTimesWithPlaceholder = () => {
      const container = editor?.editorContainer;
      if (!container) return;

      const selectButtons = container.querySelectorAll('.tox-toolbar__primary button.tox-tbtn--select');
      for (const btn of selectButtons) {
        const labelEl = btn.querySelector('.tox-tbtn__select-label');
        if (!labelEl) continue;

        const text = (labelEl.textContent || '').trim();
        if (text === 'Times New Roman') {
          labelEl.textContent = PLACEHOLDER;
        }

        const finalText = (labelEl.textContent || '').trim();
        if (finalText === PLACEHOLDER) {
          btn.classList.add('is-font-placeholder');
        } else {
          btn.classList.remove('is-font-placeholder');
        }
      }
    };
    const scheduleReplace = () => {
      setTimeout(replaceTimesWithPlaceholder, 0);
    };
    editor.on('init', scheduleReplace);
    editor.on('PostRender', scheduleReplace);
    editor.on('SetContent', scheduleReplace);
    editor.on('NodeChange', scheduleReplace);
    editor.on('SelectionChange', scheduleReplace);
    editor.on('init', () => {
      const container = editor?.editorContainer;
      if (!container || typeof MutationObserver === 'undefined') return;
      observer = new MutationObserver(scheduleReplace);
      observer.observe(container, {
        subtree: true,
        childList: true,
        characterData: true,
      });
    });

    editor.on('remove', () => {
      if (observer) observer.disconnect();
      observer = undefined;
    });
  },

};


watch(editorValue, (newValue) => {
  emit('update:content', normalizeContentColors(newValue));
});
watch(() => props.content, (newValue) => {
  if (newValue && newValue !== editorValue.value) {
    editorValue.value = normalizeContentColors(newValue);
  }
});
</script>

<style scoped>
.tinymce-wrapper :deep(.tox-menubar) {
  display: flex;
  gap: 16px;
  padding: 0px 6px;
}
.tinymce-wrapper :deep(.tox-menubar .tox-mbtn__select-label) {
  color: #171717 !important;
  font-size: 15px !important;
  font-weight: 500 !important;
}

.tinymce-wrapper :deep(.tox .tox-mbtn--active .tox-mbtn__select-label),
.tinymce-wrapper :deep(.tox .tox-mbtn:not(:disabled).tox-mbtn--active:focus .tox-mbtn__select-label) {
  color: #ffffff !important;
}
.tinymce-wrapper :deep(.tox .tox-mbtn--active),
tinymce-wrapper :deep(.tox .tox-mbtn--active:hover),
.tinymce-wrapper :deep(.tox .tox-mbtn:not(:disabled).tox-mbtn--active:focus) {
  border-radius: px;
  background: #C32E0E;
  border: 0;
  box-shadow: none;
  color: #ffffff;
}

.tinymce-wrapper :deep(.tox-toolbar__group) {
  margin-top: 8px;
  display: flex;
  justify-content: start;
  align-items: center;
  margin-left: 0px;
  margin-right: 0px;
}
.tinymce-wrapper :deep(.tox-toolbar__group:not(:last-of-type)) {
  border-right: none;
}
.tinymce-wrapper :deep(.tox-menubar + .tox-toolbar-overlord .tox-toolbar__primary) {
  border-top: none;
  margin-top: 0px;
  padding: 0px 10px;
}

.tinymce-wrapper :deep(.tox-toolbar__primary) {
  background: none !important;
}
.tinymce-wrapper :deep(.tox .tox-tbtn--select.is-font-placeholder .tox-tbtn__select-label) {
  color: #757575 !important;
}
.tinymce-wrapper :deep(.tox .tox-tbtn--select .tox-tbtn__select-chevron svg path) {
  display: flex;
  align-items: center;
  fill: #872612 !important;
}

.tinymce-wrapper :deep(.tox button.tox-tbtn[data-mce-name="overflow-button"] .tox-icon svg path) {
  background: transparent !important;
  fill: #C32E0E !important;
}

.tinymce-wrapper :deep(.tox .tox-tbtn--active),
.tinymce-wrapper :deep(.tox .tox-tbtn--enabled),
.tinymce-wrapper :deep(.tox .tox-tbtn--enabled:focus),
.tinymce-wrapper :deep(.tox .tox-tbtn--enabled:hover) {
  background: none !important;
  background-color: transparent !important;
}

.tinymce-wrapper :deep(.tox-tinymce) {
    border: none;
}

.tinymce-wrapper :deep(.tox .tox-tbtn--disabled),
.tinymce-wrapper :deep(.tox .tox-tbtn--disabled:hover) {
  background: #fff2f2;
  box-shadow: none;
}

.tinymce-wrapper :deep(.tox-toolbar__primary > .tox-toolbar__group:nth-child(2) > :is(.tox-tbtn)) {
  margin: 2px 0 3px 0;
  border: 1px solid #c9c9c9;
  border-radius: 10px;
  background: #ffffff;
}
.tinymce-wrapper :deep(.tox-toolbar__primary > .tox-toolbar__group:nth-child(3) > :is(.tox-tbtn)) {
  margin: 2px 0 3px 0;
  border: 1px solid #c9c9c9;
  border-radius: 10px;
  background: #ffffff;
}

.tinymce-wrapper :deep(.tox .tox-collection--list .tox-collection__group) {
  border-color: #171717;
  border-left-width: 0;
  border-right-width: 0;
  border-style: solid;
  border-top-width: 1px;
  padding: 4px 8px;
  width: 200px !important;
}
.tinymce-wrapper :deep(.tox .tox-collection--list .tox-collection__group:first-child) {
  border-top-width: 0;
}
</style>